// === GSAP ANIMATIONS MANAGER ===
// Centralized animation system to avoid conflicts and reduce redundancy

const AnimationManager = {
  initialized: false,
  triggers: new Set(),

  init() {
    if (this.initialized || typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      console.warn('GSAP or ScrollTrigger not loaded');
      return;
    }

    gsap.registerPlugin(ScrollTrigger);
    this.initialized = true;

    this.initHero();
    this.initRevealElements();
    this.initCascadeGroups();
    this.initSportSection();
    this.initShortWigsSwiper();
    this.initCollectionsScroll();
    this.initRussianHairSection();
    this.initBuyersGuide();
    this.initTooltips();

    // ------------------------
    // Refresh ScrollTrigger after everything is initialized (desktop only)
    // ------------------------
    if (window.innerWidth >= 1024) {
      window.addEventListener('load', () => ScrollTrigger.refresh());

      let resizeTimeout;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => ScrollTrigger.refresh(), 400);
      });
    }
  },

  // === HERO SECTION ===
  initHero() {
    gsap.utils.toArray('.hero').forEach(hero => {
      const container = hero.querySelector('.hero__container');
      if (!container) return;

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: hero,
          start: 'top top',
          end: '+=200',
          scrub: 0.7,
          invalidateOnRefresh: true,
          onUpdate: self => {
            if (self.progress === 0) {
              gsap.set(container, { scale: 1 });
            }
          }
        }
      });

      tl.to(container, {
        scale: 0.95,
        ease: 'cubic-bezier(0.39, 0.575, 0.565, 1)',
      });
    });
  },

  // === GENERIC REVEAL ELEMENTS ===
  initRevealElements() {
    const revealElements = gsap.utils.toArray('.media-block__media, .reveal-text');
    if (revealElements.length === 0) return;

    revealElements.forEach(el => {
      gsap.set(el, { y: 30, opacity: 0 });
      gsap.to(el, {
        y: 0,
        opacity: 1,
        duration: 1.2,
        ease: 'power4.out',
        scrollTrigger: {
          trigger: el,
          start: 'top 80%',
          toggleActions: 'play none none none',
          once: true,
          invalidateOnRefresh: true
        }
      });
    });
  },

  // === CASCADE ANIMATIONS ===
  initCascadeGroups() {
    const groups = [
      { container: '.collection-list', items: '.collection-list__item' },
      { container: '.resource-list__carousel', items: '.resource-list__item' },
      { container: '.animated-video-group', items: '.animated-video' }
    ];

    groups.forEach(group => {
      gsap.utils.toArray(group.container).forEach(container => {
        const items = container.querySelectorAll(group.items);
        if (items.length === 0) return;

        gsap.set(items, { opacity: 0, y: 30 });
        gsap.to(items, {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power2.out',
          stagger: 0.15,
          scrollTrigger: {
            trigger: container,
            start: 'top 85%',
            once: true,
            invalidateOnRefresh: true
          }
        });
      });
    });

    // Handle standalone animated videos
    const standaloneVideos = gsap.utils.toArray('.animated-video:not([data-in-group])');
    if (standaloneVideos.length > 0) {
      gsap.set(standaloneVideos, { opacity: 0, y: 30 });
      gsap.to(standaloneVideos, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power2.out',
        stagger: 0.2,
        scrollTrigger: {
          trigger: standaloneVideos[0],
          start: 'top 85%',
          once: true,
          invalidateOnRefresh: true
        }
      });
    }
  },

  // === SPORT SECTION HORIZONTAL SCROLL ===
  initSportSection() {
    const section = document.querySelector('[data-sport-section]');
    if (!section) return;

    const container = section.querySelector('[data-sport-container]');
    const blocks = container?.querySelectorAll('.sport-section__block');
    if (!blocks || blocks.length < 2) return;

    const blockWidth = window.innerWidth;
    const totalScroll = (blocks.length - 1) * blockWidth;
    const isMobile = window.innerWidth < 1024;

    // Kill old triggers
    ScrollTrigger.getAll().forEach(t => {
      if (t.vars.trigger === section) t.kill();
    });

    // Horizontal scroll
    gsap.to(container, {
      x: -totalScroll,
      ease: 'none',
      scrollTrigger: {
        trigger: section,
        start: 'top top',
        end: () => '+=' + totalScroll,
        pin: true,
        pinSpacing: true,
        scrub: this.isMobile ? 0.5 : 1.2,
        anticipatePin: 1, // KEY: prevent jump
        invalidateOnRefresh: true,
        fastScrollEnd: this.isMobile
      }
    });

    // Simplify block animations - one animation for all
    gsap.set(blocks, { opacity: 0 });
    gsap.to(blocks, {
      opacity: 1,
      duration: 0.4,
      ease: 'power1.out',
      scrollTrigger: {
        trigger: section,
        start: 'top 95%',
        once: true
      }
    });
  },

  // === SHORT WIGS SWIPER ===
  initShortWigsSwiper() {
    if (typeof Swiper === 'undefined') return;

    const swiperContainer = document.querySelector('.short-wigs-swiper-container');
    if (!swiperContainer) return;

    new Swiper('.short-wigs-swiper-container', {
      loop: false,
      speed: 800,
      effect: 'coverflow',
      grabCursor: true,
      slidesPerView: 'auto',
      centeredSlides: true,
      autoplay: { delay: 3600 },
      coverflowEffect: {
        rotate: 0,
        stretch: 0,
        depth: 140,
        modifier: 1,
        scale: 0.92,
        slideShadows: false
      },
      watchSlidesProgress: true,
      on: {
        slideChange: (sw) => this.updateSwiperLabel(sw),
        autoplayTimeLeft: (sw) => this.updateSwiperProgress(sw)
      }
    });

    // Init label
    const activeLabel = document.querySelector('.short-wigs-swiper-pagination-labels .page-active');
    if (activeLabel) activeLabel.textContent = '01';
  },

  updateSwiperLabel(swiper) {
    const total = swiper.slides.length - (swiper.loop ? 2 * swiper.loopedSlides : 0);
    const real = ((swiper.realIndex % total) + total) % total;
    const activeLabel = document.querySelector('.page-active');
    if (activeLabel) activeLabel.textContent = (real + 1).toString().padStart(2, '0');
  },

  updateSwiperProgress(swiper) {
    const bar = document.querySelector('.short-wigs-swiper-pagination-progress-bar');
    if (bar) bar.style.width = Math.round(swiper.autoplay.timeLeft / swiper.params.autoplay.delay * 100) + '%';
  },

  // === COLLECTIONS SCROLL WITH DOTS ===
  initCollectionsScroll() {
    const section = document.querySelector('[data-collections-scroll]');
    if (!section) return;

    const scrollContainer = section.querySelector('[data-scroll-container]');
    const dots = section.querySelectorAll('[data-dot]');
    const cards = section.querySelectorAll('[data-collection-card]');

    if (!scrollContainer || dots.length === 0) return;

    // Update active dot on scroll
    scrollContainer.addEventListener('scroll', () => {
      const scrollLeft = scrollContainer.scrollLeft;
      const containerWidth = scrollContainer.offsetWidth;
      const activeIndex = Math.round(scrollLeft / containerWidth) + 1;

      dots.forEach(dot => dot.classList.remove('active'));
      const activeDot = section.querySelector(`[data-dot="${activeIndex}"]`);
      if (activeDot) activeDot.classList.add('active');
    });

    // Dot click navigation
    dots.forEach(dot => {
      dot.addEventListener('click', () => {
        const index = dot.dataset.dot;
        const containerWidth = scrollContainer.offsetWidth;
        scrollContainer.scrollTo({
          left: (index - 1) * containerWidth,
          behavior: 'smooth'
        });
      });
    });

    // Card content reveal
    cards.forEach(card => {
      const content = card.querySelector('[data-card-content]');
      if (!content) return;

      gsap.set(content, { opacity: 0, y: 20 });
      gsap.to(content, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: card,
          start: 'left center',
          once: true
        }
      });
    });
  },

  // === RUSSIAN HAIR SECTION ===
  initRussianHairSection() {
    const section = document.querySelector('[data-russian-hair]');
    if (!section) return;

    const imageContainer = section.querySelector('[data-image-container]');
    const content = section.querySelector('[data-content]');

    if (!imageContainer || !content) return;

    const isMobile = window.innerWidth < 1024;
    const clipStart = isMobile ? 'top 85%' : 'top 95%';
    const clipEnd = isMobile ? 'top 60%' : 'top 30%';
    const clipPercent = isMobile ? '100%' : '25%';

    // Clip-path reveal
    gsap.fromTo(imageContainer, {
      clipPath: 'polygon(100% 0, 100% 100%, 100% 100%, 100% 0)'
    }, {
      clipPath: `polygon(100% 0, 100% 100%, ${clipPercent} 100%, ${clipPercent} 0)`,
      ease: 'power2.inOut',
      scrollTrigger: {
        trigger: section,
        start: clipStart,
        end: clipEnd,
        scrub: true,
        invalidateOnRefresh: true
      }
    });

    // Parallax effect
    gsap.fromTo(imageContainer, {
      y: 0
    }, {
      y: isMobile ? '-40px' : '-20vh',
      ease: 'none',
      scrollTrigger: {
        trigger: section,
        start: isMobile ? 'top bottom' : 'center center',
        end: isMobile ? 'bottom top' : 'bottom center',
        scrub: true,
        invalidateOnRefresh: true
      }
    });

    // Content reveal
    gsap.fromTo(content, {
      y: isMobile ? 70 : '70vh',
      opacity: 0
    }, {
      y: 0,
      opacity: 1,
      duration: 1.1,
      ease: isMobile ? 'power3.out' : 'power2.out',
      scrollTrigger: {
        trigger: content,
        start: 'top 85%',
        toggleActions: 'play none none reverse',
        invalidateOnRefresh: true
      }
    });
  },

  // === BUYERS GUIDE SECTION ===
  initBuyersGuide() {
    const section = document.querySelector('[data-buyers-guide]');
    if (!section) return;

    const content = section.querySelector('[data-content-reveal]');
    if (!content) return;

    gsap.from(content, {
      duration: 1.2,
      opacity: 0,
      scale: 0.95,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: section,
        start: 'top 80%',
        toggleActions: 'play none none reverse',
        invalidateOnRefresh: true
      }
    });
  },

  // === TOOLTIP MODALS ===
  initTooltips() {
    const triggers = document.querySelectorAll('.tooltip-trigger');
    if (triggers.length === 0) return;

    triggers.forEach(btn => {
      btn.addEventListener('click', () => this.openTooltip(btn));
    });

    const closeBtn = document.getElementById('tooltip-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closeTooltip());
    }

    document.addEventListener('click', e => {
      const isTrigger = e.target.closest('.tooltip-trigger');
      const isModal = e.target.closest('.tooltip-modal');
      const backdrop = document.getElementById('tooltip-backdrop');
      if (!isTrigger && !isModal && backdrop && !backdrop.hidden) {
        this.closeTooltip();
      }
    });
  },

  openTooltip(btn) {
    const content = btn.getAttribute('data-tooltip-content');
    const modal = document.getElementById('tooltip-content');
    const backdrop = document.getElementById('tooltip-backdrop');

    if (modal && content) modal.innerHTML = content;
    if (backdrop) {
      backdrop.hidden = false;
      setTimeout(() => backdrop.classList.add('show'), 10);
    }
  },

  closeTooltip() {
    const backdrop = document.getElementById('tooltip-backdrop');
    if (backdrop) {
      backdrop.classList.remove('show');
      setTimeout(() => {
        backdrop.hidden = true;
        const modal = document.getElementById('tooltip-content');
        if (modal) modal.innerHTML = '';
      }, 300);
    }
  },
};

// === INITIALIZE ON DOM READY ===
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => AnimationManager.init());
} else {
  AnimationManager.init();
}
